import { create } from 'zustand';
import {
  dismissEmailActivity,
  dismissAllEmailActivity,
  getEmailProcessingActivity,
  retryEmailActivity,
  setAllEmailActivityRead,
  setEmailActivityRead
} from '../api/notifications.api';
import { EmailProcessingActivity } from '../api/notifications.api';

export type NotificationSeverity = 'success' | 'error' | 'info' | 'warning';

export interface UiNotification {
  id: string;
  title: string;
  message: string;
  severity: NotificationSeverity;
  createdAt: string;
  read: boolean;
}

function transactionDetails(
  summary: EmailProcessingActivity['summary']
): string[] {
  if (!summary) return [];
  return Object.entries(summary).flatMap(([symbol, entries]) =>
    entries
      .filter((entry) => Boolean(entry.row))
      .map((entry) => {
        const row = entry.row!;
        return `${(row.type || 'TRANSACTION').toUpperCase()} - ${
          row.symbol || symbol
        } - ${row.quantity ?? ''}`;
      })
  );
}

interface NotificationState {
  notifications: UiNotification[];
  addNotification: (
    notification: Omit<UiNotification, 'id' | 'createdAt' | 'read'>
  ) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  markAllNotificationsUnread: () => void;
  dismissNotification: (id: string) => void;
  clearNotifications: () => void;
  loadEmailActivity: () => Promise<void>;
  retryEmailNotification: (id: string) => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],

  addNotification: (notification) => {
    const nextNotification: UiNotification = {
      ...notification,
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      createdAt: new Date().toISOString(),
      read: false
    };

    set((state) => ({
      notifications: [nextNotification, ...state.notifications].slice(0, 20)
    }));
  },

  markNotificationRead: (id) => {
    if (id.startsWith('email-')) {
      void setEmailActivityRead(Number(id.slice('email-'.length)), true).catch(
        () => undefined
      );
    }
    set((state) => ({
      notifications: state.notifications.map((item) =>
        item.id === id ? { ...item, read: true } : item
      )
    }));
  },

  markAllNotificationsRead: () => {
    void setAllEmailActivityRead(true).catch(() => undefined);
    set((state) => ({
      notifications: state.notifications.map((item) => ({
        ...item,
        read: true
      }))
    }));
  },

  markAllNotificationsUnread: () => {
    void setAllEmailActivityRead(false).catch(() => undefined);
    set((state) => ({
      notifications: state.notifications.map((item) => ({
        ...item,
        read: false
      }))
    }));
  },

  dismissNotification: (id) => {
    if (id.startsWith('email-')) {
      void dismissEmailActivity(Number(id.slice('email-'.length))).catch(
        () => undefined
      );
    }
    set((state) => ({
      notifications: state.notifications.filter((item) => item.id !== id)
    }));
  },

  clearNotifications: () => {
    void dismissAllEmailActivity().catch(() => undefined);
    set({ notifications: [] });
  },

  loadEmailActivity: async () => {
    try {
      const activity = await getEmailProcessingActivity();
      const persisted = activity.map((item): UiNotification => {
        const title =
          item.status === 'FAILED'
            ? 'Email processing failed'
            : item.status === 'PROCESSING'
            ? 'Email processing'
            : item.failedRecords
            ? 'Transaction import needs attention'
            : 'Transaction detected';
        const details = transactionDetails(item.summary);
        const message =
          item.status === 'FAILED'
            ? item.errorMessage ||
              'The email could not be processed. Retry the email import.'
            : item.status === 'PROCESSING'
            ? 'The forwarded trade confirmation is being processed.'
            : item.failedRecords
            ? `${item.failedRecords} transaction(s) failed to import. Retry the email import.`
            : `${
                item.processedRecords || 0
              } transaction(s) imported successfully.${
                details.length ? `\n${details.join('\n')}` : ''
              }`;

        return {
          id: `email-${item.id}`,
          title,
          message,
          severity:
            item.status === 'FAILED' || item.failedRecords
              ? 'error'
              : 'success',
          createdAt: item.createdAt,
          read: Boolean(item.readAt)
        };
      });

      set((state) => ({
        notifications: [
          ...state.notifications.filter(
            (item) => !item.id.startsWith('email-')
          ),
          ...persisted
        ]
          .sort(
            (left, right) =>
              Date.parse(right.createdAt) - Date.parse(left.createdAt)
          )
          .slice(0, 50)
      }));
    } catch {
      // Keep local notifications available when email activity is temporarily unreachable.
    }
  },

  retryEmailNotification: async (id) => {
    if (!id.startsWith('email-')) return;
    await retryEmailActivity(Number(id.slice('email-'.length)));
    await useNotificationStore.getState().loadEmailActivity();
  }
}));
